/**
 * The Acquisition page's programme audits: referrals (state, reward, and why
 * a reward was withheld) and the Distributed by Spark deal (who has it on).
 * Names and emails arrive only for admin and support; an analyst sees ids.
 */

import {
  Alert,
  EmptyState,
  Spinner,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@charisol-spark/ui";
import { Gift } from "@phosphor-icons/react";
import {
  useAdminDistributedBySpark,
  useAdminReferrals,
} from "@/hooks/useAdmin";
import type { AdminReferralRow } from "@/services/admin";
import { getErrorMessage } from "@/services/api";

/** What the Acquisition toolbar narrows the two programme tables by. */
export interface ProgrammeFilters {
  /** Text to find in an account's email, name or id. */
  search?: string;
  /** Only referrals in this lifecycle stage. */
  referralState?: AdminReferralRow["state"];
  /** Only deal accounts that are on, or off. */
  dealStatus?: "on" | "off";
  /** Only rows dated on or after this day (YYYY-MM-DD). */
  from?: string;
  /** Only rows dated on or before this day (YYYY-MM-DD). */
  to?: string;
}

function inRange(iso: string | null, filters: ProgrammeFilters): boolean {
  if (!filters.from && !filters.to) return true;
  const d = iso?.slice(0, 10);
  if (!d) return false;
  return (
    (!filters.from || d >= filters.from) && (!filters.to || d <= filters.to)
  );
}

function includesText(text: string, parts: Array<string | null | undefined>) {
  const needle = text.toLowerCase();
  return parts.filter(Boolean).join(" ").toLowerCase().includes(needle);
}

const STATE_LABEL: Record<AdminReferralRow["state"], string> = {
  signed_up: "Signed up",
  published: "Published",
  paid: "Paid",
};

/** What the withheld reasons mean, in the words an admin needs. */
const WITHHELD_LABEL: Record<string, string> = {
  self_referral: "Same account",
  same_email: "Same mailbox as the referrer",
  same_company: "Same company domain as the referrer",
};

function day(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "—";
}

function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border/50 bg-card p-4 sm:p-6">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      <p className="mb-4 mt-1 text-xs text-muted-foreground">{description}</p>
      {children}
    </section>
  );
}

function Totals({ items }: { items: Array<[string, number]> }) {
  return (
    <dl className="mb-4 flex flex-wrap gap-x-8 gap-y-2">
      {items.map(([label, value]) => (
        <div key={label}>
          <dt className="text-xs text-muted-foreground">{label}</dt>
          <dd className="text-xl font-semibold tabular-nums">
            {value.toLocaleString()}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ReferralsPanel({ filters }: { filters: ProgrammeFilters }) {
  const { data, isLoading, error } = useAdminReferrals();
  const referrals = [...(data?.referrals ?? [])]
    .filter(
      (r) =>
        (!filters.referralState || r.state === filters.referralState) &&
        inRange(r.signedUpAt, filters) &&
        (!filters.search ||
          includesText(filters.search, [
            r.referrerEmail,
            r.referrerId,
            r.inviteeEmail,
            r.inviteeId,
            r.inviteeName,
          ]))
    )
    .sort((a, b) => (a.signedUpAt < b.signedUpAt ? 1 : -1));
  const narrowed = Boolean(
    filters.search || filters.referralState || filters.from || filters.to
  );
  return (
    <Panel
      title="Referrals"
      description="Every invite link sign-up. The referrer's 500 credits land on the invitee's first payment above zero; a withheld reward shows why, and the invitee is not told."
    >
      {isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error || !data ? (
        <Alert variant="error">
          {getErrorMessage(error, "Failed to load referrals.")}
        </Alert>
      ) : (
        <>
          <Totals
            items={[
              ["Referrals", data.totals.referrals],
              ["Verified", data.totals.activated],
              ["Subscribed", data.totals.paid],
              ["Withheld", data.totals.withheld],
              ["Credits granted", data.totals.creditsGranted],
              ["Credits pending", data.totals.creditsPending],
            ]}
          />
          {referrals.length === 0 ? (
            <EmptyState
              icon={Gift}
              title={
                narrowed
                  ? "No referrals match your filters."
                  : "No referral sign-ups yet."
              }
              className="py-8"
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Referrer</TH>
                  <TH>Invitee</TH>
                  <TH>Joined</TH>
                  <TH>State</TH>
                  <TH>Reward</TH>
                </TR>
              </THead>
              <TBody>
                {referrals.map((r) => (
                  <TR key={`${r.referrerId}-${r.inviteeId}`}>
                    <TD>{r.referrerEmail ?? r.referrerId}</TD>
                    <TD>
                      {r.inviteeEmail ?? r.inviteeId}
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({r.inviteeName})
                      </span>
                    </TD>
                    <TD className="tabular-nums text-muted-foreground">
                      {day(r.signedUpAt)}
                    </TD>
                    <TD>{STATE_LABEL[r.state]}</TD>
                    <TD>
                      {r.withheldReason ? (
                        <StatusPill tone="amber">
                          Withheld:{" "}
                          {WITHHELD_LABEL[r.withheldReason] ?? r.withheldReason}
                        </StatusPill>
                      ) : r.rewardedAt ? (
                        <StatusPill tone="green">
                          {(r.rewardCredits ?? 0).toLocaleString()} credits
                          {" on "}
                          {day(r.rewardedAt)}
                        </StatusPill>
                      ) : (
                        <StatusPill>Pending</StatusPill>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </>
      )}
    </Panel>
  );
}

function DealPanel({ filters }: { filters: ProgrammeFilters }) {
  const { data, isLoading, error } = useAdminDistributedBySpark();
  const accounts = [...(data?.accounts ?? [])]
    .filter(
      (a) =>
        (!filters.dealStatus || a.enabled === (filters.dealStatus === "on")) &&
        inRange(a.enabledSince, filters) &&
        (!filters.search || includesText(filters.search, [a.email, a.userId]))
    )
    .sort((a, b) => ((a.enabledSince ?? "") < (b.enabledSince ?? "") ? 1 : -1));
  const narrowed = Boolean(
    filters.search || filters.dealStatus || filters.from || filters.to
  );
  return (
    <Panel
      title="Distributed by Spark"
      description="Accounts that chose the deal: a Distributed by Spark comment on their LinkedIn posts for 5 credits a post. It is never on by default."
    >
      {isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error || !data ? (
        <Alert variant="error">
          {getErrorMessage(
            error,
            "Failed to load the Distributed by Spark deal."
          )}
        </Alert>
      ) : (
        <>
          <Totals
            items={[
              ["On now", data.totals.enabled],
              ["Ever on", data.totals.everEnabled],
              ["Comments posted", data.totals.commentsPosted],
              ["Comments failed", data.totals.commentsFailed],
            ]}
          />
          {accounts.length === 0 ? (
            <EmptyState
              icon={Gift}
              title={
                narrowed
                  ? "No deal accounts match your filters."
                  : "Nobody has turned the deal on."
              }
              className="py-8"
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Account</TH>
                  <TH>Status</TH>
                  <TH>On since</TH>
                  <TH className="text-right">Posted</TH>
                  <TH className="text-right">Failed</TH>
                </TR>
              </THead>
              <TBody>
                {accounts.map((a) => (
                  <TR key={a.userId}>
                    <TD>{a.email ?? a.userId}</TD>
                    <TD>
                      <StatusPill tone={a.enabled ? "green" : "neutral"}>
                        {a.enabled ? "On" : "Off"}
                      </StatusPill>
                    </TD>
                    <TD className="tabular-nums text-muted-foreground">
                      {day(a.enabledSince)}
                    </TD>
                    <TD className="text-right tabular-nums">
                      {a.commentsPosted.toLocaleString()}
                    </TD>
                    <TD className="text-right tabular-nums text-muted-foreground">
                      {a.commentsFailed.toLocaleString()}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </>
      )}
    </Panel>
  );
}

export function AcquisitionPrograms({
  filters = {},
}: {
  filters?: ProgrammeFilters;
}) {
  return (
    <>
      <ReferralsPanel filters={filters} />
      <DealPanel filters={filters} />
    </>
  );
}
